
import type {ComponentType} from 'react';
import React, {PureComponent} from 'react';
import {
    formatDate,
    DateFormats,
    stringToNumber,
} from '@pb/kit-formatters';
import {DataTypes} from './constants';
import {getDisplayValue} from './getDisplayValue';
import type {InputControlValue} from './typedef';
import type {DataType} from './constants';
import type {TypedInputProps as OriginalTypedInputProps} from '../typed-input/typed-input.component';
import {TypedInput} from '../typed-input/typed-input.component';
import type {CustomInputProps} from '../suffixed-input/suffixed-input.component';
import {SuffixedInput} from '../suffixed-input/suffixed-input.component';
import {formatDateRange} from './utils/formatDateRange';


export type Options = {
    suffix?: string,
    roundOff?: boolean,
    showZeros?: boolean,
    absoluteValue?: boolean,
    truncateDecimals?: boolean,
    hideTrailingZeros?: boolean,
    negativeParenthesis?: boolean,
    decimals?: number,
    threeDigit?: boolean,
};

export type Money = {
    currency: string,
    amount: number,
    asOfDate?: string,
    nativeCurrency?: string,
    nativeAmount?: number,
    currencyRate?: number,
    estimated?: boolean,
};

type TypedInputProps = Omit<OriginalTypedInputProps, 'value' | 'onBlur' | 'inputComponent'>;

export interface DataTypedInputProps extends TypedInputProps {

    /**
     * Input value
     */
    value?: InputControlValue,

    /**
     * Input data type
     */
    dataType: DataType,

    /**
     * User locale
     */
    locale: OriginalTypedInputProps['locale'] & {
        currency: string,
        dateFormat: string,
        symbolPosition: string,
    },

    /**
     * onBlur callback
     */
    onBlur?: (formattedValue: InputControlValue, event?: React.FocusEvent<HTMLInputElement>) => void,

    /**
     * Value configuration
     */
    valueConfig?: Options,

    inputRef?: React.Ref<HTMLInputElement>,
}

type State = {
    displayValue: {
        value: string,
        suffix?: string,
    },
};

/**
* @deprecated It will be removed in 6 months 06-11-2026. Use NumberFormattedInput from "@pb/kit-component-formatted-input" instead.
* @see [NumberFormattedInput]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-formatted-input-number--documentation}
*
* @description
* Component for input with predefined data type and Locale.
* - Formats value based on dataType, validateType and locale.
* - Returns formatted value on blur.
*
* ### Migration to V2
* `@pb/kit-component-data-typed-input` is deprecated and will be removed in the future.
* - DataTypedInput is copied from @pb/kit-component-data-typed-input.
* - DataTypedInput - tooltip is removed, compose with Tooltip outside.
* - DataTypedInput - autoComplete is removed, use Autocomplete instead.
* - DataTypedInput - input props are merged with rest of props.
*
* ### Accessibility
* It accepts all native input attributes that are available in HTML. Please follow standard requirement for accessibility, e.g. https://www.w3.org/WAI/tutorials/forms/instructions/.
* - Use `aria-label` or `aria-labelledby` to provide a label for the input.
* - Use `aria-describedby` to provide a description for the input.
* */
export const DataTypedInput = class DataTypedInput extends PureComponent<DataTypedInputProps, State> {
    state = {
        displayValue: getDisplayValue(
            this.convertPropToType(this.props.value),
            this.props.dataType,
            this.props.locale,
            this.props.valueConfig,
        ),
    };

    componentDidUpdate(prevProps: DataTypedInputProps) {
        const {value, dataType, locale} = this.props;

        if (prevProps.value !== value) {
            this.setState({
                displayValue: getDisplayValue(
                    this.convertPropToType(value),
                    dataType,
                    locale,
                    this.props.valueConfig,
                ),
            });
        }
    }

    getMoney(value: string): Money | null | undefined {
        if (value === '') {
            return null;
        }

        return {
            amount: stringToNumber(value, this.props.locale),
            currency: this.props.locale.currency,
        };
    }

    convertPropToType(value: InputControlValue): InputControlValue {
        const {dataType} = this.props;

        switch (dataType) {
            case DataTypes.THOUSANDS:
            case DataTypes.MILLION:
            case DataTypes.CURRENCY:
                if (typeof value === 'string' || typeof value === 'number') {
                    return this.getMoney(`${value}`);
                }

                return value;

            default:
                return value;
        }
    }

    convertToType(value: string) {
        const {dataType} = this.props;

        switch (dataType) {
            case DataTypes.THOUSANDS:
            case DataTypes.MILLION:
            case DataTypes.CURRENCY:
                return this.getMoney(value);

            case DataTypes.PERCENT:
            case DataTypes.MULTIPLES:
            case DataTypes.NUMBER:
                return value !== ''
                    ? stringToNumber(value, this.props.locale)
                    : null;

            case DataTypes.DATE:
                return value !== ''
                    ? formatDate(
                            value,
                            'YYYY-MM-DD',
                            DateFormats[this.props.locale.dateFormat] || this.props.locale.dateFormat,
                        )
                    : null;

            case DataTypes.DATE_RANGE:
                return formatDateRange(
                    value,
                    this.props.locale.dateFormat,
                ).value;

            default:
                return value;
        }
    }

    onBlur = (value: string, event: React.FocusEvent<HTMLInputElement>) => {
        this.props.onBlur?.(this.onUpdate(value), event);
    };

    onUpdate = (value: string) => {
        const inputControlValue = this.convertToType(value);
        this.setState({
            displayValue: getDisplayValue(
                inputControlValue,
                this.props.dataType,
                this.props.locale,
                this.props.valueConfig,
            ),
        });
        return inputControlValue;
    };

    render() {
        const {
            valueConfig,
            dataType,
            locale,
            validateType: validateTypeProp,
            inputRef,
            ...restProps
        } = this.props;
        const suffix = valueConfig?.suffix ?? this.state.displayValue.suffix ?? '';

        const inputProps = {
            ...restProps,
            value: this.state.displayValue.value,
            onBlur: (event: React.FocusEvent<HTMLInputElement>) => this.onBlur(event.target.value, event),
        };

        let validateType = validateTypeProp;
        if (dataType === DataTypes.DATE) {
            validateType = 'date';
        }
        if (dataType === DataTypes.DATE_RANGE) {
            validateType = 'date-range';
        }

        return (
            <TypedInput
                {...inputProps}
                ref={inputRef}
                formattingOptions={valueConfig}
                // WHY: suffix is not in TypedInputProps, but is passed to SuffixedInput
                // @ts-expect-error
                suffix={suffix}
                onBlur={this.onBlur}
                validateType={validateType}
                locale={locale}
                inputComponent={SuffixedInput as unknown as ComponentType<CustomInputProps>}
            />
        );
    }
} as ComponentType<DataTypedInputProps>;
